import { createAuthClient } from 'better-auth/react';
import { inferAdditionalFields } from 'better-auth/client/plugins';
import { rememberName, rememberSession } from './session-hint';

/**
 * The identity client.
 *
 * Points at the API, because that is where Better Auth is mounted — this app
 * is static assets on a different origin and has no server of its own to host
 * an auth handler on.
 *
 * **The session is stored and sent as a bearer token, not a cookie.** The API
 * sets `credentials: false` on its CORS policy so a cookie *cannot* be sent,
 * which makes the whole surface immune to CSRF by construction. Better Auth
 * defaults to cookies; keeping that default would have quietly given back the
 * property that choice bought. `fetchOptions` below is what carries the token
 * on the auth calls themselves, and `api.ts` does the same for every other
 * request.
 */
const BASE = import.meta.env['VITE_API_URL'] ?? 'https://api.goodbuild.cc';

/** Where the session token lives between page loads. */
const TOKEN_KEY = 'goodbuild.session';

export function sessionToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Stores a session token that arrived from somewhere other than a `fetch`.
 *
 * There is exactly one such place: the launcher's sign-in, where the token
 * comes back from `POST /v1/launcher/oauth/exchange` rather than off a
 * `set-auth-token` header the client already knows how to read. It is a
 * function rather than an exported `TOKEN_KEY` on purpose — a second module
 * holding the key is a second module that can spell it differently, and the
 * failure that produces is a sign-in that succeeds and a reload that is signed
 * out.
 */
export function storeSessionToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export const authClient = createAuthClient({
  baseURL: `${BASE}/auth`,
  /**
   * No `oauthPopupClient`. Social sign-in is driven by `signInWithProvider`
   * below, against the same server plugin — the note there explains why the
   * library's client half cannot work against a bearer-only API.
   */
  plugins: [inferAdditionalFields()],
  fetchOptions: {
    /**
     * **Do not ask for cookies.** Better Auth's client defaults to
     * `credentials: 'include'`, and this API answers `credentials: false` on
     * purpose — that is what makes the surface immune to CSRF by construction
     * rather than by a check somebody has to remember to write.
     *
     * The two are not merely redundant together, they are incompatible: a
     * browser refuses a cross-origin response to a credentialed request unless
     * `Access-Control-Allow-Credentials: true` comes back, so *every* call to
     * `/auth/*` was blocked — including the `get-session` that `useSession`
     * makes on every page. Sign in successfully, reload, and the session fetch
     * is blocked, so the root gate reads "signed out" and sends you back to
     * sign in.
     *
     * Nothing is lost by omitting them. The session travels as a bearer token
     * below, which is the entire point of the plugin pairing in
     * `apps/api/src/identity.ts`.
     */
    credentials: 'omit',
    auth: {
      type: 'Bearer',
      token: () => sessionToken() ?? '',
    },
    onSuccess: (context) => {
      // Better Auth returns the session in this header on sign-in and sign-up.
      // Storing it here is what makes the bearer scheme work across reloads;
      // with cookies the browser would do it and CSRF would be back.
      const token = context.response.headers.get('set-auth-token');
      if (token) localStorage.setItem(TOKEN_KEY, token);
    },
  },
});

export const { signIn, signUp, signOut, useSession } = authClient;

/**
 * Clears the stored token as well as the server session.
 *
 * And the storefront's two cookies, which are the half that is easy to forget
 * because nothing on this origin reads either of them. `Shell`'s effect would
 * clear them a render later anyway; doing it here means a sign-out that
 * navigates straight to `goodbuild.cc` cannot arrive before the effect does.
 *
 * **The name goes with the hint, always, and never on its own** (#113). Leaving
 * `gb_name` behind would be a store header that had stopped saying somebody was
 * signed in while still carrying what they are called, on a machine they have
 * just signed out of and quite possibly handed back — which is the one failure
 * a display-name cookie can actually cause. The storefront refuses to read the
 * name unless the hint says signed in, so this is the belt beside that brace
 * rather than the only thing standing between them.
 */
export async function endSession(): Promise<void> {
  await signOut();
  localStorage.removeItem(TOKEN_KEY);
  rememberSession(false);
  rememberName(null);
}

/**
 * Social sign-in, driven from here rather than by `signIn.popup`.
 *
 * The server half of `oauthPopup` is exactly what we want: the popup runs the
 * flow first-party to the API, and its completion page posts
 * `{ type, nonce, token }` back to this window. The *client* half is written
 * for two topologies, and this app is neither.
 *
 * `signIn.popup` branches on `isEmbedded()`, which is true only inside a
 * cross-origin iframe. A top-level app takes the other branch: it calls
 * `clearPopupToken()` — discarding the token it was just handed — and then
 * verifies by fetching `/get-session`, on the assumption that the session
 * cookie the callback set on the auth origin will be sent along. Ours never
 * can be: the API answers `credentials: false` precisely so that no cookie is
 * ever sent, which is the CSRF property the bearer scheme exists to keep. So
 * `/get-session` answers 200 with `data: null`, and the plugin reports
 * `POPUP_SIGN_IN_FAILED` — after a sign-in that fully succeeded, with the user
 * row, the linked account and the session all written. The only thing that
 * failed was the check.
 *
 * That is why this reimplements forty lines rather than configuring around
 * them: what we need is the token the popup already posts, under the key
 * `api.ts` reads. Nothing here reaches into the library's internals — it
 * speaks the same postMessage contract the completion page does.
 */

/** The `type` the completion page posts. Part of the server plugin's contract. */
const POPUP_MESSAGE = 'better-auth:oauth-popup';

/** Long enough to read a consent screen and find a password manager. */
const POPUP_TIMEOUT_MS = 300_000;

/** How often to notice a window that was closed without finishing. */
const CLOSED_POLL_MS = 500;

export type SocialResult = { ok: true } | { ok: false; message: string };

/**
 * What each failure means, in words that say what to do about it.
 *
 * The codes arrive from three places now — the popup-start endpoint's own
 * `fail()`, the `error` parameter Better Auth puts on the callback's redirect,
 * and the same parameter arriving over loopback in the launcher — and all of
 * them are machine strings. `account_not_linked` is the one somebody is most
 * likely to hit and least likely to decode: it means the email address is
 * already registered, and the way out is to sign in the original way and
 * connect the provider afterwards, which no amount of pressing the button
 * again will discover.
 *
 * **One map, read by both clients.** `describeProviderError` below is what the
 * launcher uses, and it exists so that there is no second set of sentences for
 * the same set of codes — two vocabularies for one failure is how the desktop
 * half ends up saying something the browser half stopped saying two years ago.
 */
const MESSAGES: Record<string, string> = {
  access_denied: 'You cancelled that at the provider, so nothing changed.',
  account_not_linked:
    'An account already uses that email address. Sign in with your password first, then connect this provider from your account settings.',
  email_not_verified:
    'That provider has not verified your email address, so it cannot be used to sign in.',
  invalid_callback_url:
    'This app is misconfigured: it asked to return to an address the API does not trust.',
  provider_not_found: 'That provider is not configured on this deployment.',
  popup_sign_in_failed: 'The provider could not be reached. Try again in a moment.',
  signup_disabled:
    'That provider cannot create new accounts here. Sign up with an email address first.',
  unable_to_create_user:
    'The account could not be created. Try again, or use an email address instead.',
};

/** `some_new_code` -> `Some new code.`, for anything MESSAGES has no entry for. */
function readable(code: string): string {
  const words = code.replace(/[_-]+/g, ' ').trim();
  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}.` : 'That did not work.';
}

/**
 * One provider error code, as a sentence.
 *
 * The launcher's path has no `description` to fall back on — a loopback
 * redirect carries the code and nothing else — so this is the two-step version
 * of what `onMessage` does below: the map first, then the code rendered as
 * English. Exported so that the desktop and browser flows cannot drift apart on
 * what `account_not_linked` means.
 */
export function describeProviderError(code: string): string {
  return MESSAGES[code] ?? readable(code);
}

function nonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function popupFeatures(): string {
  const width = 500;
  const height = 650;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  return `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no`;
}

/**
 * Runs one provider sign-in and stores the session it hands back.
 *
 * **Opens the window before its first `await`**, deliberately: a browser only
 * honours `window.open` while it still attributes the call to the click, and
 * awaiting anything first is the difference between a popup and a blocked one
 * — which on screen looks like the button doing nothing at all.
 */
export function signInWithProvider(provider: string, callbackURL: string): Promise<SocialResult> {
  const authOrigin = new URL(BASE).origin;
  const expected = nonce();

  const start = new URL(`${BASE}/auth/oauth-popup/start`);
  start.searchParams.set('provider', provider);
  start.searchParams.set('popupOrigin', window.location.origin);
  start.searchParams.set('popupNonce', expected);
  /*
   * Absolute, because the API builds the return URL and a bare path would
   * resolve against *its* origin. Both this and `popupOrigin` are checked
   * against `TRUSTED_ORIGINS` before the flow starts.
   */
  start.searchParams.set('callbackURL', callbackURL);

  const popup = window.open(start.toString(), 'goodbuild-oauth', popupFeatures());
  if (!popup) {
    return Promise.resolve({
      ok: false,
      message: 'Your browser blocked the sign-in window. Allow popups for this site and try again.',
    });
  }

  return new Promise<SocialResult>((resolve) => {
    let settled = false;

    const settle = (result: SocialResult): void => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(poll);
      clearTimeout(timer);
      try {
        if (!popup.closed) popup.close();
      } catch {
        // Still cross-origin on the provider's page. It closes itself.
      }
      resolve(result);
    };

    function onMessage(event: MessageEvent): void {
      /*
       * Origin, type and nonce are all checked. Any page may post to this
       * window, and what arrives here is a session token — treating an
       * unchecked message as one would be an account takeover, not a bug.
       */
      if (event.origin !== authOrigin) return;

      const data = event.data as {
        type?: string;
        nonce?: string;
        token?: string;
        error?: { code?: string; description?: string };
      } | null;

      if (data?.type !== POPUP_MESSAGE || data.nonce !== expected) return;

      if (data.error) {
        const code = data.error.code ?? '';
        settle({ ok: false, message: MESSAGES[code] ?? data.error.description ?? readable(code) });
        return;
      }

      if (typeof data.token !== 'string' || data.token === '') return;

      // The whole point of the exercise: the key the rest of the app reads.
      localStorage.setItem(TOKEN_KEY, data.token);
      settle({ ok: true });
    }

    const poll = setInterval(() => {
      if (popup.closed)
        settle({ ok: false, message: 'The sign-in window closed before it finished.' });
    }, CLOSED_POLL_MS);

    const timer = setTimeout(
      () => settle({ ok: false, message: 'That took too long. Try again.' }),
      POPUP_TIMEOUT_MS,
    );

    window.addEventListener('message', onMessage);
  });
}
